import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Drawer, Input, Button, Spin, Empty, Tabs, Select } from 'antd';
import { SearchOutlined, AppstoreOutlined, BarsOutlined, FileImageOutlined, VideoCameraOutlined, FolderOutlined, LoadingOutlined, SoundOutlined, BoxPlotOutlined } from '@ant-design/icons';
import { useInfiniteMediaGenerations, useInfiniteStorageFiles } from '../../../api/canvasApi';
import MediaPreviewModal from '../../editor/components/MediaPreviewModal';

const AssetsDrawer = ({ visible, onClose }) => {
    // State
    const [searchText, setSearchText] = useState('');
    const [viewMode, setViewMode] = useState('grid');
    const [filterType, setFilterType] = useState('all');
    const [filterCanvas, setFilterCanvas] = useState('all');
    const [filterFolder, setFilterFolder] = useState('all');
    const [drawerWidth, setDrawerWidth] = useState(420);

    // Preview state
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewStartIndex, setPreviewStartIndex] = useState(0);
    const [previewMediaList, setPreviewMediaList] = useState([]);



    // Infinite queries — only active when drawer is visible
    const {
        data: generationsData,
        fetchNextPage: fetchNextGenerations,
        hasNextPage: hasMoreGenerations,
        isFetchingNextPage: isFetchingMoreGenerations,
        isLoading: generationsLoading
    } = useInfiniteMediaGenerations(null, 24, filterType === 'all' ? null : filterType);

    const {
        data: uploadsData,
        fetchNextPage: fetchNextUploads,
        hasNextPage: hasMoreUploads,
        isFetchingNextPage: isFetchingMoreUploads,
        isLoading: uploadsLoading
    } = useInfiniteStorageFiles(visible ? 'media-uploads' : null, 24);

    const {
        data: inspirationData,
        fetchNextPage: fetchNextInspiration,
        hasNextPage: hasMoreInspiration,
        isFetchingNextPage: isFetchingMoreInspiration,
        isLoading: inspirationLoading
    } = useInfiniteStorageFiles(visible ? 'inspiration-from-the-web' : null, 24);

    const loading = uploadsLoading || generationsLoading || inspirationLoading;

    const handleMediaClick = (index, list) => {
        setPreviewStartIndex(index);
        setPreviewMediaList(list);
        setPreviewVisible(true);
    };

    // Resize handling
    const isResizing = useRef(false);

    const handleMouseDown = (e) => {
        e.preventDefault();
        isResizing.current = true;
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = useCallback((e) => {
        if (!isResizing.current) return;
        const newWidth = window.innerWidth - e.clientX;
        if (newWidth >= 340 && newWidth <= 1200) {
            setDrawerWidth(newWidth);
        }
    }, []);

    const handleMouseUp = useCallback(() => {
        isResizing.current = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove]);

    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    // Transform helpers
    const toStorageItem = (file) => {
        const ext = file.name ? file.name.split('.').pop().toLowerCase() : '';
        let type = 'image';
        if (['mp4', 'webm', 'mov'].includes(ext)) type = 'video';
        if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) type = 'audio';
        if (['glb', 'gltf'].includes(ext)) type = 'model';

        return {
            id: file.path,
            type,
            src: file.url,
            label: file.name,
            canvasId: file.customMetadata?.canvasId || 'unknown',
            canvasName: file.customMetadata?.canvasName || 'Unknown',
            metadata: file.customMetadata || {},
            createdAt: file.timeCreated
        };
    };

    const toGenerationItem = (gen) => {
        return {
            id: gen.id,
            generationId: gen.generation_id,
            type: gen.type || 'image',
            src: gen.url,
            label: gen.prompt ? gen.prompt.slice(0, 30) : 'Generated Media',
            canvasId: gen.canvas_id || 'unknown',
            canvasName: 'Unknown',
            prompt: gen.prompt || '',
            model: gen.model || '',
            provider: gen.provider || '',
            metadata: gen.metadata || {},
            createdAt: gen.created_at
        };
    };

    const toInspirationItem = (file) => {
        const ext = file.name ? file.name.split('.').pop().toLowerCase() : '';
        let type = 'image';
        if (['mp4', 'webm', 'mov'].includes(ext)) type = 'video';

        return {
            id: file.path,
            type,
            src: file.url,
            label: file.name,
            path: file.path,
            folder: file.folder || 'uncategorized',
            notes: file.customMetadata?.notes || '',
            metadata: file.customMetadata || {}
        };
    };

    // Flatten paginated data
    const importedMedia = useMemo(() => {
        if (!uploadsData?.pages) return [];
        return uploadsData.pages.flatMap(page => page.data.map(f => toStorageItem(f)));
    }, [uploadsData]);

    const generatedMedia = useMemo(() => {
        if (!generationsData?.pages) return [];
        return generationsData.pages.flatMap(page => page.data.map(f => toGenerationItem(f)));
    }, [generationsData]);

    const inspirationMedia = useMemo(() => {
        if (!inspirationData?.pages) return [];
        return inspirationData.pages.flatMap(page => page.data.map(f => toInspirationItem(f)));
    }, [inspirationData]);

    // Get unique canvases and folders
    const availableCanvases = useMemo(() => {
        const canvases = new Set();
        [...importedMedia, ...generatedMedia].forEach(item => {
            if (item.canvasName && item.canvasName !== 'Unknown') {
                canvases.add(item.canvasName);
            }
        });
        return Array.from(canvases).sort();
    }, [importedMedia, generatedMedia]);

    const inspirationFolders = useMemo(() => {
        return [...new Set(inspirationMedia.map(item => item.folder))].filter(Boolean);
    }, [inspirationMedia]);

    // Filtered media
    const filteredImportedMedia = useMemo(() => {
        return importedMedia
            .filter(item => filterType === 'all' || item.type === filterType)
            .filter(item => filterCanvas === 'all' || item.canvasName === filterCanvas)
            .filter(item => !searchText || item.label?.toLowerCase().includes(searchText.toLowerCase()));
    }, [importedMedia, filterType, filterCanvas, searchText]);

    const filteredGeneratedMedia = useMemo(() => {
        return generatedMedia
            .filter(item => filterType === 'all' || item.type === filterType)
            .filter(item => filterCanvas === 'all' || item.canvasName === filterCanvas)
            .filter(item => !searchText ||
                item.label?.toLowerCase().includes(searchText.toLowerCase()) ||
                item.prompt?.toLowerCase().includes(searchText.toLowerCase()));
    }, [generatedMedia, filterType, filterCanvas, searchText]);

    const filteredInspirationMedia = useMemo(() => {
        return inspirationMedia
            .filter(item => filterType === 'all' || item.type === filterType)
            .filter(item => filterFolder === 'all' || item.folder === filterFolder)
            .filter(item => !searchText ||
                item.label?.toLowerCase().includes(searchText.toLowerCase()) ||
                item.notes?.toLowerCase().includes(searchText.toLowerCase()));
    }, [inspirationMedia, filterType, filterFolder, searchText]);

    // Custom hook for per-sentinel infinite scroll with callback refs
    const useInfiniteScroll = (fetchNext, hasMore, isFetching) => {
        const [node, setNode] = useState(null);
        const sentinelRef = useCallback((el) => setNode(el), []);

        useEffect(() => {
            if (!node || !hasMore || isFetching) return;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) fetchNext();
                },
                { threshold: 0, rootMargin: '0px 0px 400px 0px' }
            );

            observer.observe(node);
            return () => observer.disconnect();
        }, [node, fetchNext, hasMore, isFetching]);

        return sentinelRef;
    };

    const importedSentinelRef = useInfiniteScroll(fetchNextUploads, hasMoreUploads, isFetchingMoreUploads);
    const generatedSentinelRef = useInfiniteScroll(fetchNextGenerations, hasMoreGenerations, isFetchingMoreGenerations);
    const inspirationSentinelRef = useInfiniteScroll(fetchNextInspiration, hasMoreInspiration, isFetchingMoreInspiration);

    // Load more spinner
    const LoadMoreSpinner = ({ isFetching }) => (
        isFetching ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />
            </div>
        ) : null
    );

    // Render media item
    const renderMediaItem = (item, index, list) => (
        <div
            key={item.id}
            onClick={() => handleMediaClick(index, list)}
            style={{
                position: 'relative',
                aspectRatio: viewMode === 'grid' ? '1/1' : '16/9',
                borderRadius: 8,
                overflow: 'hidden',
                border: '1px solid #30363d',
                cursor: 'pointer',
                background: '#161b22'
            }}
        >
            {item.type === 'audio' ? (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1c2128', color: '#eb2f96' }}>
                    <SoundOutlined style={{ fontSize: 32 }} />
                </div>
            ) : item.type === 'model' ? (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1c2128', color: '#52c41a' }}>
                    <BoxPlotOutlined style={{ fontSize: 32 }} />
                </div>
            ) : item.type === 'video' ? (
                <video src={item.src} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
            ) : (
                <img src={item.src} alt={item.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" decoding="async" />
            )}
            {/* Overlay */}
            <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                padding: '24px 8px 8px',
                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end'
            }}>
                {item.folder && (
                    <span style={{ fontSize: 10, color: '#8b949e', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <FolderOutlined style={{ fontSize: 10 }} />
                        {item.folder}
                    </span>
                )}
                {item.type === 'video' && <VideoCameraOutlined style={{ color: '#1890ff', fontSize: 12 }} />}
                {item.type === 'audio' && <SoundOutlined style={{ color: '#eb2f96', fontSize: 12 }} />}
            </div>
        </div>
    );

    // Render media grid with sentinel for infinite scroll
    const renderMediaGrid = (media, sentinelRef, isFetchingMore) => (
        <>
            <div style={{
                display: 'grid',
                gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(120px, 1fr))' : '1fr',
                gap: 12
            }}>
                {media.map((item, index) => renderMediaItem(item, index, media))}
            </div>
            {/* Sentinel element for intersection observer */}
            <div ref={sentinelRef} style={{ height: 20 }} />
            <LoadMoreSpinner isFetching={isFetchingMore} />
        </>
    );

    return (
        <>
            <Drawer
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <span style={{ color: '#c9d1d9', fontSize: 12, fontWeight: 600, letterSpacing: '0.5px' }}>MEDIA LIBRARY</span>
                        <div style={{ display: 'flex', gap: 2 }}>
                            <div
                                onClick={() => setViewMode('grid')}
                                style={{
                                    width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: viewMode === 'grid' ? 'rgba(24, 144, 255, 0.1)' : 'transparent',
                                    borderRadius: 4, cursor: 'pointer', transition: 'all 0.2s',
                                    color: viewMode === 'grid' ? '#1890ff' : '#8b949e'
                                }}
                            >
                                <AppstoreOutlined style={{ fontSize: 14 }} />
                            </div>
                            <div
                                onClick={() => setViewMode('list')}
                                style={{
                                    width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: viewMode === 'list' ? 'rgba(24, 144, 255, 0.1)' : 'transparent',
                                    borderRadius: 4, cursor: 'pointer', transition: 'all 0.2s',
                                    color: viewMode === 'list' ? '#1890ff' : '#8b949e'
                                }}
                            >
                                <BarsOutlined style={{ fontSize: 14 }} />
                            </div>
                        </div>
                    </div>
                }
                closable={false}
                placement="right"
                onClose={onClose}
                open={visible}
                getContainer={false}
                mask={true}
                maskStyle={{ background: 'transparent' }}
                width={drawerWidth}
                style={{ position: 'absolute', background: '#0d1117', borderLeft: '1px solid #30363d' }}
                headerStyle={{ borderBottom: '1px solid #30363d', background: '#161b22', padding: '16px 24px', height: 60, display: 'flex', alignItems: 'center' }}
                bodyStyle={{ background: '#0d1117', padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            >
                {/* Resizer Handle */}
                <div
                    onMouseDown={handleMouseDown}
                    style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 6,
                        cursor: 'col-resize',
                        zIndex: 100,
                        background: 'transparent',
                        transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.background = 'rgba(24, 144, 255, 0.3)'}
                    onMouseLeave={(e) => {
                        if (!isResizing.current) e.target.style.background = 'transparent';
                    }}
                />

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                    {loading && !importedMedia.length && !generatedMedia.length && !inspirationMedia.length ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                        </div>
                    ) : (
                        <Tabs
                            defaultActiveKey="1"
                            centered
                            tabBarStyle={{
                                color: '#8b949e',
                                borderBottom: '1px solid #30363d',
                                margin: 0,
                                fontSize: 13,
                                background: '#0d1117',
                                zIndex: 10
                            }}
                            style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
                            className="full-height-tabs"
                            items={[
                                {
                                    key: '1',
                                    label: 'Imported',
                                    children: (
                                        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                                            {/* Filters */}
                                            <div style={{ padding: '16px', borderBottom: '1px solid #30363d', background: '#161b22' }}>
                                                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                                                    <Input
                                                        prefix={<SearchOutlined style={{ color: '#8b949e' }} />}
                                                        placeholder="Search..."
                                                        onChange={e => setSearchText(e.target.value)}
                                                        style={{ flex: 1, background: '#0d1117', borderColor: '#30363d' }}
                                                        allowClear
                                                    />
                                                </div>
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    <Select
                                                        value={filterType}
                                                        onChange={setFilterType}
                                                        style={{ flex: 1 }}
                                                        dropdownStyle={{ background: '#21262d', border: '1px solid #30363d' }}
                                                        options={[
                                                            { value: 'all', label: 'All Types' },
                                                            { value: 'image', label: 'Images' },
                                                            { value: 'video', label: 'Videos' },
                                                            { value: 'audio', label: 'Audio' },
                                                            { value: 'model', label: '3D Models' }
                                                        ]}
                                                    />
                                                    {availableCanvases.length > 0 && (
                                                        <Select
                                                            value={filterCanvas}
                                                            onChange={setFilterCanvas}
                                                            style={{ flex: 1 }}
                                                            dropdownStyle={{ background: '#21262d', border: '1px solid #30363d' }}
                                                            options={[
                                                                { value: 'all', label: 'All Canvases' },
                                                                ...availableCanvases.map(c => ({ value: c, label: c }))
                                                            ]}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            {/* Media Grid */}
                                            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
                                                {filteredImportedMedia.length === 0 && !uploadsLoading ? (
                                                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ color: '#8b949e' }}>No imported media</span>} />
                                                ) : renderMediaGrid(filteredImportedMedia, importedSentinelRef, isFetchingMoreUploads)}
                                            </div>
                                        </div>
                                    )
                                },
                                {
                                    key: '2',
                                    label: 'Generated',
                                    children: (
                                        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                                            {/* Filters */}
                                            <div style={{ padding: '16px', borderBottom: '1px solid #30363d', background: '#161b22' }}>
                                                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                                                    <Input
                                                        prefix={<SearchOutlined style={{ color: '#8b949e' }} />}
                                                        placeholder="Search prompts..."
                                                        onChange={e => setSearchText(e.target.value)}
                                                        style={{ flex: 1, background: '#0d1117', borderColor: '#30363d' }}
                                                        allowClear
                                                    />
                                                </div>
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    <Select
                                                        value={filterType}
                                                        onChange={setFilterType}
                                                        style={{ flex: 1 }}
                                                        dropdownStyle={{ background: '#21262d', border: '1px solid #30363d' }}
                                                        options={[
                                                            { value: 'all', label: 'All Types' },
                                                            { value: 'image', label: 'Images' },
                                                            { value: 'video', label: 'Videos' },
                                                            { value: 'audio', label: 'Audio' },
                                                            { value: 'model', label: '3D Models' }
                                                        ]}
                                                    />
                                                </div>
                                            </div>
                                            {/* Media Grid */}
                                            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
                                                {filteredGeneratedMedia.length === 0 && !generationsLoading ? (
                                                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ color: '#8b949e' }}>No generated media</span>} />
                                                ) : renderMediaGrid(filteredGeneratedMedia, generatedSentinelRef, isFetchingMoreGenerations)}
                                            </div>
                                        </div>
                                    )
                                },
                                {
                                    key: '3',
                                    label: 'Inspiration',
                                    children: (
                                        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                                            {/* Filters */}
                                            <div style={{ padding: '16px', borderBottom: '1px solid #30363d', background: '#161b22' }}>
                                                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                                                    <Input
                                                        prefix={<SearchOutlined style={{ color: '#8b949e' }} />}
                                                        placeholder="Search inspiration..."
                                                        onChange={e => setSearchText(e.target.value)}
                                                        style={{ flex: 1, background: '#0d1117', borderColor: '#30363d' }}
                                                        allowClear
                                                    />
                                                </div>
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    <Select
                                                        value={filterType}
                                                        onChange={setFilterType}
                                                        style={{ flex: 1 }}
                                                        dropdownStyle={{ background: '#21262d', border: '1px solid #30363d' }}
                                                        options={[
                                                            { value: 'all', label: 'All Types' },
                                                            { value: 'image', label: 'Images' },
                                                            { value: 'video', label: 'Videos' }
                                                        ]}
                                                    />
                                                    {inspirationFolders.length > 0 && (
                                                        <Select
                                                            value={filterFolder}
                                                            onChange={setFilterFolder}
                                                            style={{ flex: 1 }}
                                                            dropdownStyle={{ background: '#21262d', border: '1px solid #30363d' }}
                                                            options={[
                                                                { value: 'all', label: 'All Folders' },
                                                                ...inspirationFolders.map(f => ({ value: f, label: f.charAt(0).toUpperCase() + f.slice(1) }))
                                                            ]}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            {/* Media Grid */}
                                            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
                                                {filteredInspirationMedia.length === 0 && !inspirationLoading ? (
                                                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ color: '#8b949e' }}>No inspiration found</span>} />
                                                ) : renderMediaGrid(filteredInspirationMedia, inspirationSentinelRef, isFetchingMoreInspiration)}
                                            </div>
                                        </div>
                                    )
                                }
                            ]}
                        />
                    )}
                </div>
            </Drawer>
            <MediaPreviewModal
                visible={previewVisible}
                onClose={() => setPreviewVisible(false)}
                initialIndex={previewStartIndex}
                mediaItems={previewMediaList}
            />
        </>
    );
};

export default AssetsDrawer;
